import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { playButtonDownSound, triggerHapticFeedback, getSoundManager } from "@/utils/effects"

// Helper to check if we're on a route that doesn't need audio
const isAudioDisabledRoute = () => {
  const pathname = window.location.pathname;
  return pathname.includes('/dashboard') || pathname.includes('/teams');
};

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        accent: "bg-accent text-accent-foreground hover:bg-accent/90",
        success: "bg-emerald-500 text-white hover:bg-emerald-600",
        warning: "bg-amber-500 text-white hover:bg-amber-600",
        info: "bg-sky-500 text-white hover:bg-sky-600"
      },
      size: {
        default: "h-10 px-4 py-3",
        xs: "h-8 rounded-md px-2 py-1.5 text-xs",
        sm: "h-9 rounded-md px-3 py-2",
        lg: "h-11 rounded-md px-8 py-3",
        xl: "h-14 rounded-md px-10 py-4 text-base",
        icon: "h-10 w-10 p-2.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    // Use a ref instead of state so toggling the debounce flag does NOT
    // trigger a re-render mid-gesture (which was cancelling the click on
    // the first tap on touch devices).
    const hasTriggeredAudioRef = React.useRef(false);

    // Combine the refs
    const setRefs = React.useCallback((element: HTMLButtonElement | null) => {
      buttonRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    }, [ref]);

    // Setup direct DOM event listeners for mobile, but only for non-dashboard/teams routes.
    // Mount-only: the listener does not need to be rebuilt when the debounce flag changes.
    React.useEffect(() => {
      if (!buttonRef.current || isAudioDisabledRoute()) return;

      const node = buttonRef.current;
      const handleDirectTouchStart = () => {
        if (isAudioDisabledRoute() || hasTriggeredAudioRef.current) return;

        hasTriggeredAudioRef.current = true;
        const soundManager = getSoundManager();
        soundManager.runAfterUnlock(() => {
          playButtonDownSound();
          triggerHapticFeedback('light');
        });

        setTimeout(() => { hasTriggeredAudioRef.current = false; }, 100);
      };

      node.addEventListener('touchstart', handleDirectTouchStart, { passive: true });
      return () => {
        node.removeEventListener('touchstart', handleDirectTouchStart);
      };
    }, []);

    // Handle pointer down to play the sound, but only for non-dashboard/teams routes
    const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
      if (isAudioDisabledRoute() || hasTriggeredAudioRef.current) {
        if (props.onPointerDown) props.onPointerDown(e);
        return;
      }

      hasTriggeredAudioRef.current = true;

      const soundManager = getSoundManager();
      soundManager.runAfterUnlock(() => {
        playButtonDownSound();
        triggerHapticFeedback('light');
      });

      setTimeout(() => { hasTriggeredAudioRef.current = false; }, 100);

      if (props.onPointerDown) props.onPointerDown(e);
    };

    const Comp = asChild ? Slot : "button"

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={setRefs}
        onPointerDown={handlePointerDown}
        {...props}
      >
        {children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
